const DEFAULT_DECIMAL_PLACES = 2;

/**
 * Rounds a plain (unsigned) decimal string to `decimalPlaces`, half-up, using
 * BigInt string arithmetic — never a float multiply/divide — so it doesn't
 * inherit binary floating-point drift (e.g. 1.005 incorrectly rounding to 1.00).
 * @param value - An unsigned decimal string, e.g. "1.239" or "12".
 * @param decimalPlaces - Target decimal digits.
 * @returns {string} The rounded decimal string.
 */
function roundDecimalString(value: string, decimalPlaces: number): string {
  const [intPartRaw, decPartRaw = ""] = value.split(".");
  const intPart = intPartRaw === "" ? "0" : intPartRaw;
  const keep = decPartRaw.slice(0, decimalPlaces).padEnd(decimalPlaces, "0");
  const nextDigit = decPartRaw[decimalPlaces] ?? "0";
  let scaled = BigInt(intPart) * 10n ** BigInt(decimalPlaces) + BigInt(keep === "" ? "0" : keep);
  if (Number(nextDigit) >= 5) {
    scaled += 1n;
  }
  const scaledString = scaled.toString().padStart(decimalPlaces + 1, "0");
  if (decimalPlaces === 0) {
    return scaledString;
  }
  const newIntPart = scaledString.slice(0, scaledString.length - decimalPlaces);
  const newDecPart = scaledString.slice(scaledString.length - decimalPlaces);
  return `${newIntPart}.${newDecPart}`;
}

/**
 * Decimal-safe rounding for a number, half-up, sidestepping binary
 * floating-point drift (e.g. Math.round(1.005 * 100) / 100 wrongly gives 1).
 * Routes through a high-precision string (toFixed) before rounding on digits.
 * @param value - The number to round.
 * @param decimalPlaces - Target decimal digits.
 * @returns {number} The rounded number.
 */
export function roundToDecimals(value: number, decimalPlaces: number = DEFAULT_DECIMAL_PLACES): number {
  if (Number.isNaN(value)) {
    return value;
  }
  const sign = value < 0 ? -1 : 1;
  const guardDigits = Math.min(decimalPlaces + 6, 20);
  const rounded = roundDecimalString(Math.abs(value).toFixed(guardDigits), decimalPlaces);
  return sign * Number(rounded);
}

const usdFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Formats a plain unsigned dollar amount, e.g. `$1,234.56`.
 * @param amount - The amount to format.
 * @returns {string} The formatted amount.
 */
export function formatCurrency(amount: number): string {
  return usdFormatter.format(amount);
}

/**
 * Formats a dollar amount with an inferred `+`/`-` sign, e.g. `+ $1,234.56`
 * or `- $1,234.56`. For a nonzero amount, the sign is inferred from the
 * value itself. For a zero amount, `+0`/`-0` aren't distinguishable via
 * `amount < 0`, so `negativeZero` lets a caller force the "-" display for a
 * conceptually-negative zero (e.g. a $0 deduction) — it's ignored for any
 * nonzero amount, since that sign already speaks for itself.
 * @param amount - The amount to format.
 * @param negativeZero - Only consulted when `amount` is exactly 0; pass
 *   `true` to render "- $0.00" instead of the default "+ $0.00".
 * @returns {string} The signed, formatted amount.
 */
export function formatSignedCurrency(amount: number, negativeZero = false): string {
  const isNegative = amount < 0 || (amount === 0 && negativeZero);
  return `${isNegative ? "-" : "+"} ${usdFormatter.format(Math.abs(amount))}`;
}

/**
 * Formats a plain numeric rate as a percentage, e.g. `19.99%`.
 * @param rate - The rate to format (already expressed as a percentage, e.g. 19.99 for 19.99%).
 * @returns {string} The formatted percentage.
 */
export function formatPercent(rate: number): string {
  return `${rate}%`;
}

/**
 * Sums a list of `{ amount }` items, decimal-safe (see roundToDecimals).
 * @param {{ amount: number }[]} items - The items to sum.
 * @returns {number} The rounded sum.
 */
export function sumAmounts(items: Array<{ amount: number }>): number {
  return roundToDecimals(items.reduce((sum, item) => sum + item.amount, 0));
}
